import { useEffect, useMemo, useRef, useState } from "react"
import { useDimensions } from "./dimensions"
import {
    DiasDisponibles,
    useGetDisponibilidadHabitacionesQuery,
    useGetDisponibilidadTarifasQuery,
    useGetDisponibilidadTurnosAtencionQuery,
    useListenCheckDisponibilidadHabitacionesSubscription,
    EstadosTurnosAtencion,
    GetDisponibilidadTarifasQuery,
    TipoTarifa,
} from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"

const days = [
    DiasDisponibles.Domingo,
    DiasDisponibles.Lunes,
    DiasDisponibles.Martes,
    DiasDisponibles.Miercoles,
    DiasDisponibles.Jueves,
    DiasDisponibles.Viernes,
    DiasDisponibles.Sabado,
]

const getCardsLength = (height: number, width: number, isListaEspera = false) => {
    const card = isListaEspera && width > height ? 118 : (width <= 1280 ? 160 : 234)
    const elements = isListaEspera && width > height
        ? (height - 120) / (card + 20)
        : width < height
            ? (height - 750) / (card + 70)
            : (height - 48) / (card + 20)

    return Math.floor(elements)
}

const getItems = (data: any, height: number, width: number, isListaEspera = false): any[] => {
    const list: any[] = []

    const length = getCardsLength(height, width, isListaEspera)

    for (let i = 0; i <= data.length; i += length) {
        list.push(data.slice(i, i + length))
    }

    return list.filter((item) => item && item.length > 0)
}

const getCapitalizeText = (text: string) => {
    let value = ""
    text?.split(" ")?.forEach((v) => {
        const item = v?.charAt(0)?.toUpperCase() + v?.slice(1)?.toLowerCase()
        value = `${value}${value.length > 0 ? " " : ""}${item}`
    })

    return value
}

const getFolioNumber = (folio: string): number => {
    const match = folio?.match(/(\d+)/)
    return match ? parseInt(match[1], 10) : 0
}

// Mayor costo entre las tarifas de reserva visibles en pantalla para el día: prioridad a `reserva`, si no hay se usa `renta_reserva`
const getPrecioReserva = (
    tarifas: GetDisponibilidadTarifasQuery["tarifas"],
    tipo_habitacion_id: string,
    day: DiasDisponibles
): number => {
    const delTipo = tarifas.filter((t) => t?.tipo_habitacion_id === tipo_habitacion_id && t?.dias_disponibles?.includes(day))
    const reserva = delTipo.filter((t) => t?.tipo_tarifa === TipoTarifa.Reserva)
    const candidatas = reserva.length > 0 ? reserva : delTipo.filter((t) => t?.tipo_tarifa === TipoTarifa.RentaReserva)
    return candidatas.length > 0 ? Math.max(...candidatas.map((t) => Number(t?.costo_habitacion || 0))) : 0
}

export function useData() {
    const { height, width } = useDimensions()
    const { hotel_id, logo_hotel, configurations } = useProfile()
    const { loading, data, refetch } = useGetDisponibilidadHabitacionesQuery({
        variables: {
            hotel_id,
        },
    })

    const { loading: loadTarifas, data: tarifas } = useGetDisponibilidadTarifasQuery({
        variables: {
            hotel_id,
        },
    })

    const {
        loading: loadTurnos,
        data: turnosData,
        refetch: refetchTurnos,
    } = useGetDisponibilidadTurnosAtencionQuery({
        variables: {
            hotel_id,
            estado: [EstadosTurnosAtencion.EnEspera, EstadosTurnosAtencion.EnCurso],
        },
    })

    const [subscriptionListaEspera, setSubscriptionListaEspera] = useState<{
        lista: { nombreHabitacion: string; folios: string[] }[]
        folioTurno: string
    } | null>(null)

    const refetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (turnosData?.turnos_atencion) {
            setSubscriptionListaEspera(null)
        }
    }, [turnosData])

    useListenCheckDisponibilidadHabitacionesSubscription({
        variables: { hotel_id },
        onSubscriptionData(options) {
            if (refetchDebounceRef.current) clearTimeout(refetchDebounceRef.current)
            refetchDebounceRef.current = setTimeout(() => {
                refetch()
                refetchTurnos()
            }, 1000)

            const sub = (options.subscriptionData.data?.checkDisponibilidadHabitaciones?.lista_de_espera as any)
            if (!sub) return

            const agrupado: Record<string, { nombre: string; folios: string[] }> = {}
            sub.lista?.forEach((item: any) => {
                const folios: string[] = item.turnos_atencion
                    ?.filter((t: any) => t.estado === EstadosTurnosAtencion.EnEspera && t.folio_turno)
                    ?.map((t: any) => t.folio_turno as string) ?? []
                if (folios.length > 0 && item.tipo_habitacion_id) {
                    agrupado[item.tipo_habitacion_id] = { nombre: item.nombre ?? "Sin nombre", folios }
                }
            })

            const lista = Object.values(agrupado)
                .sort((a, b) => getFolioNumber(a.folios[0]) - getFolioNumber(b.folios[0]))
                .map((item) => ({ nombreHabitacion: item.nombre, folios: item.folios }))

            if (lista.length > 0) {
                setSubscriptionListaEspera({ lista, folioTurno: sub.turno_en_curso?.folio_turno ?? "" })
            }
        },
    })

    const itemList = useMemo(() => {
        const items: any[] = []
        const day = days[new Date().getDay()]

        const habitaciones = data?.disponibilidad_habitaciones || []
        const array = [...new Map(habitaciones?.map((obj) => [obj.tipo_habitacion_id, obj])).values()]
        const tarifas_list = tarifas?.tarifas?.filter((t) => t?.pantalla_disponibilidad) || []

        array.forEach((item) => {
            // Tarifa de renta del tipo para el día: prioridad a `renta`, si no hay se usa `renta_reserva`
            const delTipo = (t: (typeof tarifas_list)[number]) =>
                t?.tipo_habitacion_id === item?.tipo_habitacion_id && t?.dias_disponibles?.includes(day)
            const tarifa =
                tarifas_list.find((t) => delTipo(t) && t?.tipo_tarifa === TipoTarifa.Renta) ||
                tarifas_list.find((t) => delTipo(t) && t?.tipo_tarifa === TipoTarifa.RentaReserva)
            if (tarifa) {
                items.push({
                    ...item,
                    tarifa,
                    priceBooking: getPrecioReserva(tarifas_list, item?.tipo_habitacion_id, day),
                })
            }
        })
        return items
    }, [data?.disponibilidad_habitaciones, tarifas?.tarifas])

    const { turnosListaEspera, folioTurno } = useMemo(() => {
        const turnos = turnosData?.turnos_atencion ?? []
        const enEspera = turnos.filter((t) => t.estado === EstadosTurnosAtencion.EnEspera)
        const enCurso = turnos.filter((t) => t.estado === EstadosTurnosAtencion.EnCurso)

        const agrupado: Record<string, { nombre: string; folios: string[] }> = {}
        enEspera.forEach((t) => {
            const id = t.tipo_habitacion_id
            const tipo = t.tipo_habitacion?.nombre ?? "Sin nombre"
            if (!id || !t.folio_turno) return
            if (!agrupado[id]) agrupado[id] = { nombre: tipo, folios: [] }
            agrupado[id].folios.push(t.folio_turno)
        })

        const listaEspera = Object.values(agrupado)
            .sort((a, b) => getFolioNumber(a.folios[0]) - getFolioNumber(b.folios[0]))
            .map((item) => ({ nombreHabitacion: item.nombre, folios: item.folios }))

        const listaEsperaAgrupada = getItems(listaEspera, height, width, true)

        const folioTurnoActual = enCurso[0]?.folio_turno ?? ""

        return {
            turnosListaEspera: listaEsperaAgrupada,
            folioTurno: folioTurnoActual,
        }
    }, [turnosData])

    return {
        logo: logo_hotel || "",
        loading: loading || loadTarifas || loadTurnos,
        items: getItems(
            itemList?.map((d) => {
                return {
                    title: getCapitalizeText(d?.tarifa?.nombre || ""),
                    image: d?.foto || "",
                    hours: d?.tarifa?.duracion_renta || 0,
                    size: d?.personas_incluidas || 0,
                    disponibles: d?.disponibles || 0,
                    price: d?.tarifa?.costo_habitacion || 0,
                    priceBooking: d?.priceBooking || 0,
                    priceExtra: d.tarifa?.costo_persona_extra || 0,
                    maxExtra: d.tarifa?.personas_extra_max || 0,
                }
            }),
            height,
            width
        ),
        img_promo_horizontal: configurations?.panel?.imagen_promo?.horizontal?.length
            ? configurations.panel.imagen_promo.horizontal
            : ["https://articulos.nyc3.cdn.digitaloceanspaces.com/easyroom/emtpyDisponibilidad.png"],
        img_promo_vertical: configurations?.panel?.imagen_promo?.vertical?.length
            ? configurations.panel.imagen_promo.vertical
            : [ "https://articulos.nyc3.cdn.digitaloceanspaces.com/easyroom/emtpyDisponibilidad.png"],
        lista_espera: subscriptionListaEspera
            ? getItems(subscriptionListaEspera.lista, height, width, true)
            : turnosListaEspera,
        folio_turn: subscriptionListaEspera?.folioTurno ?? folioTurno,
    }
}
